"use client"

import type React from "react"
import { createContext, useContext, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

interface ModalContextType {
  openModal: (content: ReactNode) => void
  closeModal: () => void
  isOpen: boolean
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider")
  }
  return context
}

interface ModalProviderProps {
  children: ReactNode
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [modalContent, setModalContent] = useState<ReactNode>(null)

  const openModal = (content: ReactNode) => {
    setModalContent(content)
    setIsOpen(true)
    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden"
  }

  const closeModal = () => {
    setIsOpen(false)
    setModalContent(null)
    // Restore body scroll
    document.body.style.overflow = "unset"
    // Dispatch event to notify components that modal closed
    window.dispatchEvent(new CustomEvent('modalClosed'))
  }

  const modalPortal =
    isOpen && modalContent
      ? createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={closeModal}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

            {/* Modal content */}
            <div className="relative z-10 max-h-[90vh] max-w-[90vw] overflow-auto" onClick={(e) => e.stopPropagation()}>
              {modalContent}
            </div>
          </div>,
          document.body,
        )
      : null

  return (
    <ModalContext.Provider value={{ openModal, closeModal, isOpen }}>
      {children}
      {modalPortal}
    </ModalContext.Provider>
  )
}
